import React from "react";
import { Users, Building2, Sparkles, Wallet, Shield, HeadphonesIcon } from "lucide-react";

// Premium hero "three connection" visual:
// A slow-rotating orbit ring with three anchored nodes representing:
//   - UK Employer (top-right)
//   - Indian Employee (bottom-left)
//   - Bright EOR (bottom-right, aqua-glow accent)
// Floating glass cards (Payroll / Compliance / Onboarding / Support)
// hover around the outer edge to hint at the managed layer.
const ConnectionVisual = () => {
  return (
    <div className="relative aspect-square w-full max-w-[560px] mx-auto" data-testid="hero-connection-visual">
      {/* soft aqua glow */}
      <div className="absolute inset-8 rounded-full bg-aqua/15 blur-[80px]" aria-hidden />

      {/* rotating orbit */}
      <div className="absolute inset-0 animate-orbit-slow" aria-hidden>
        <svg viewBox="0 0 500 500" className="w-full h-full">
          <defs>
            <linearGradient id="ring-grad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#00C2C7" stopOpacity="0.6" />
              <stop offset="60%" stopColor="#CFF6F4" stopOpacity="0.15" />
              <stop offset="100%" stopColor="#00C2C7" stopOpacity="0.4" />
            </linearGradient>
          </defs>
          <circle cx="250" cy="250" r="200" fill="none" stroke="url(#ring-grad)" strokeWidth="1.5" strokeDasharray="4 8" />
          <circle cx="250" cy="250" r="150" fill="none" stroke="rgba(207,246,244,0.18)" strokeWidth="1" strokeDasharray="2 6" />
        </svg>
      </div>

      {/* static connective triangle */}
      <svg viewBox="0 0 500 500" className="absolute inset-0 w-full h-full" aria-hidden>
        <path
          d="M250 100 L410 360 L90 360 Z"
          fill="none"
          stroke="rgba(207,246,244,0.22)"
          strokeWidth="1.5"
          strokeDasharray="3 6"
        />
      </svg>

      {/* Node 1 — UK Employer */}
      <div className="absolute left-1/2 top-[8%] -translate-x-1/2 flex flex-col items-center gap-2">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-aqua/60 blur-lg animate-node-pulse" aria-hidden />
          <div className="relative w-14 h-14 rounded-full bg-aqua text-navy flex items-center justify-center shadow-aqua-glow">
            <Building2 className="w-6 h-6" strokeWidth={2} />
          </div>
        </div>
        <div className="rounded-full bg-white/10 backdrop-blur-xl border border-white/15 px-3 py-1">
          <p className="text-[11px] font-medium tracking-wide text-white">UK Employer</p>
        </div>
      </div>

      {/* Node 2 — Indian Employee */}
      <div className="absolute left-[6%] bottom-[12%] flex flex-col items-center gap-2">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-aqua/60 blur-lg animate-node-pulse" style={{ animationDelay: "0.8s" }} aria-hidden />
          <div className="relative w-14 h-14 rounded-full bg-aqua text-navy flex items-center justify-center shadow-aqua-glow">
            <Users className="w-6 h-6" strokeWidth={2} />
          </div>
        </div>
        <div className="rounded-full bg-white/10 backdrop-blur-xl border border-white/15 px-3 py-1">
          <p className="text-[11px] font-medium tracking-wide text-white">India Talent</p>
        </div>
      </div>

      {/* Node 3 — Bright EOR (centre-ish, slightly right-bottom, brand-forward) */}
      <div className="absolute right-[4%] bottom-[12%] flex flex-col items-center gap-2">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-white/40 blur-lg animate-node-pulse" style={{ animationDelay: "1.4s" }} aria-hidden />
          <div className="relative w-16 h-16 rounded-full bg-white text-navy flex items-center justify-center shadow-[0_16px_48px_rgba(0,194,199,0.4)] border-2 border-aqua/40">
            <Sparkles className="w-7 h-7 text-aqua" strokeWidth={2.2} />
          </div>
        </div>
        <div className="rounded-full bg-aqua text-navy px-3 py-1">
          <p className="text-[11px] font-semibold tracking-wide">Bright EOR</p>
        </div>
      </div>

      {/* Floating glass service cards */}
      <FloatingCard style="top-[42%] left-[-4%]" delay="0s" icon={<Wallet className="w-4 h-4 text-aqua" />} label="Payroll" />
      <FloatingCard style="top-[16%] right-[-2%]" delay="0.6s" icon={<Shield className="w-4 h-4 text-aqua" />} label="Compliance" />
      <FloatingCard style="bottom-[38%] right-[-6%]" delay="1.2s" icon={<Sparkles className="w-4 h-4 text-aqua" />} label="Onboarding" />
      <FloatingCard style="bottom-[2%] left-[38%]" delay="1.8s" icon={<HeadphonesIcon className="w-4 h-4 text-aqua" />} label="Support" />
    </div>
  );
};

const FloatingCard = ({ style, delay, icon, label }) => (
  <div
    className={`absolute ${style} rounded-2xl bg-white/8 backdrop-blur-xl border border-white/15 px-3.5 py-2 flex items-center gap-2 shadow-[0_8px_32px_rgba(0,0,0,0.18)]`}
    style={{ animation: `node-pulse 3.4s ease-in-out infinite`, animationDelay: delay }}
  >
    <div className="w-6 h-6 rounded-full bg-navy/30 flex items-center justify-center border border-aqua/30">
      {icon}
    </div>
    <span className="text-[12px] font-medium text-white/95 tracking-tight">{label}</span>
  </div>
);

export default ConnectionVisual;
